import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, Loader2, AlertCircle, RefreshCw } from 'lucide-react';
import { Button } from '../components/ui/button';

const API = process.env.REACT_APP_BACKEND_URL;

export default function BillingSuccess() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const isBeta = searchParams.get('beta') === 'true';
  const tierName = searchParams.get('tier');
  
  const [status, setStatus] = useState('checking'); // 'checking' | 'success' | 'pending' | 'error'
  const [pollCount, setPollCount] = useState(0);
  const [subscriptionInfo, setSubscriptionInfo] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const MAX_POLLS = 10;

  const verifyAndActivate = useCallback(async () => {
    // For beta subscriptions, they're already activated - just verify
    if (isBeta) {
      setSubscriptionInfo({ plan: tierName || 'Beta Access' });
      setStatus('success');
      setTimeout(() => navigate('/dashboard'), 3000);
      return true;
    }
    
    if (!sessionId) return false;
    
    try {
      // First try to verify and activate the subscription directly
      const verifyRes = await fetch(`${API}/api/billing/verify-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ session_id: sessionId })
      });
      
      if (verifyRes.ok) {
        const verifyData = await verifyRes.json();
        
        if (verifyData.success && (verifyData.status === 'activated' || verifyData.status === 'already_active')) {
          setSubscriptionInfo({
            plan: verifyData.subscription?.tier_name || 'Premium',
          });
          setStatus('success');
          
          // Auto-redirect after 3 seconds
          setTimeout(() => {
            navigate('/dashboard');
          }, 3000);
          
          return true;
        }
      }
      
      // Fallback: check subscription status
      const statusRes = await fetch(`${API}/api/subscription/status`, { credentials: 'include' });
      if (statusRes.ok) {
        const statusData = await statusRes.json();
        if (statusData.subscriptionActive) {
          setSubscriptionInfo({ plan: statusData.currentPlan });
          setStatus('success');
          setTimeout(() => navigate('/dashboard'), 3000);
          return true;
        }
      }
      
      return false;
    } catch (error) {
      console.error('Error verifying subscription:', error);
      setErrorMessage(error.message);
      return false;
    }
  }, [sessionId, isBeta, tierName, navigate]);

  useEffect(() => {
    // For beta, we don't need a session_id - it's already activated
    if (isBeta) {
      verifyAndActivate();
      return;
    }
    
    if (!sessionId) {
      setStatus('error');
      setErrorMessage('No checkout session found');
      return;
    }

    const pollSubscription = async () => {
      const isActive = await verifyAndActivate();
      
      if (!isActive && pollCount < MAX_POLLS) {
        setPollCount(prev => prev + 1);
        setTimeout(pollSubscription, 2000);
      } else if (!isActive) {
        setStatus('pending');
      }
    };

    pollSubscription();
  }, [sessionId, isBeta, verifyAndActivate, pollCount]);

  const handleRetry = () => {
    setPollCount(0);
    setStatus('checking');
  };

  const handleGoToDashboard = () => {
    navigate('/dashboard');
  };

  const handleContactSupport = () => {
    navigate('/contact');
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#192A3F] to-[#0F1E2D] flex items-center justify-center p-6">
      <div className="max-w-md w-full">
        {status === 'checking' && (
          <div className="bg-white rounded-2xl p-8 text-center shadow-xl">
            <div className="w-16 h-16 bg-[#FF9E00]/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <Loader2 className="w-8 h-8 text-[#FF9E00] animate-spin" />
            </div>
            <h1 className="text-2xl font-bold text-[#1E3A5C] mb-2">
              Confirming Your Payment
            </h1>
            <p className="text-slate-600 mb-4">
              Please wait while we activate your subscription...
            </p>
            <div className="w-full bg-slate-100 rounded-full h-2 overflow-hidden">
              <div 
                className="bg-[#FF9E00] h-full transition-all duration-500"
                style={{ width: `${(pollCount / MAX_POLLS) * 100}%` }}
              />
            </div>
            <p className="text-sm text-slate-500 mt-2">
              This usually takes just a few seconds
            </p>
          </div>
        )}

        {status === 'success' && (
          <div className="bg-white rounded-2xl p-8 text-center shadow-xl">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-green-500" />
            </div>
            <h1 className="text-2xl font-bold text-[#1E3A5C] mb-2">
              Welcome to MedStudyPro!
            </h1>
            <p className="text-slate-600 mb-6">
              Your <strong>{subscriptionInfo?.plan}</strong> subscription is now active.
              You have full access to all features.
            </p>
            <p className="text-sm text-slate-500 mb-6">
              Redirecting to your dashboard...
            </p>
            <Button
              onClick={handleGoToDashboard}
              className="w-full bg-[#FF9E00] hover:bg-[#E88D00] text-white h-12 rounded-full font-semibold"
            >
              Go to Dashboard Now
            </Button>
          </div>
        )}

        {status === 'pending' && (
          <div className="bg-white rounded-2xl p-8 text-center shadow-xl">
            <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <AlertCircle className="w-10 h-10 text-amber-500" />
            </div>
            <h1 className="text-2xl font-bold text-[#1E3A5C] mb-2">
              Still Confirming...
            </h1>
            <p className="text-slate-600 mb-6">
              We're still confirming your payment. This can sometimes take a minute or two.
              Your access will be activated automatically once confirmed.
            </p>
            <div className="space-y-3">
              <Button
                onClick={handleRetry}
                className="w-full bg-[#1E3A5C] hover:bg-[#264B73] text-white h-12 rounded-full font-semibold"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Check Again
              </Button>
              <Button
                onClick={handleGoToDashboard}
                variant="outline"
                className="w-full h-12 rounded-full"
              >
                Go to Dashboard
              </Button>
              <p className="text-sm text-slate-500">
                If the issue persists,{' '}
                <button 
                  onClick={handleContactSupport}
                  className="text-[#FF9E00] hover:underline"
                >
                  contact support
                </button>
              </p>
            </div>
          </div>
        )}

        {status === 'error' && (
          <div className="bg-white rounded-2xl p-8 text-center shadow-xl">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <AlertCircle className="w-10 h-10 text-red-500" />
            </div>
            <h1 className="text-2xl font-bold text-[#1E3A5C] mb-2">
              Something Went Wrong
            </h1>
            <p className="text-slate-600 mb-6">
              We couldn't find your checkout session. Please try subscribing again
              or contact support if you were charged.
            </p>
            <div className="space-y-3">
              <Button
                onClick={() => navigate('/pricing')}
                className="w-full bg-[#FF9E00] hover:bg-[#E88D00] text-white h-12 rounded-full font-semibold"
              >
                Back to Pricing
              </Button>
              <Button
                onClick={handleContactSupport}
                variant="outline"
                className="w-full h-12 rounded-full"
              >
                Contact Support
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
